<script lang="ts" setup>
  import { useNotifications } from '@/shared/lib'
  import { queryKeys } from '@/shared/lib/queryKeys'
  import { EditorPageHeader } from '@/shared/ui/components'
  import PageDecorationImage from '@/shared/ui/components/PageDecorationImage.vue'

  import { useProfile } from '@/entities/profile'

  import { FaqEditor, getFaqList } from '@/features/faq'

  definePage({
    meta: {
      layout: 'baseNew',
      contentManagerRoute: true,
    },
  })

  const { t } = useI18n()
  const route = useRoute('/faq/edit-item=[id]/')
  const router = useRouter()
  const itemId = computed(() => Number(route.params.id))
  const { isAdmin } = storeToRefs(useProfile())
  const { errorNotification } = useNotifications()

  const { data, isPending, isError } = useQuery({
    queryKey: queryKeys.faqList(isAdmin),
    queryFn: () => getFaqList(isAdmin.value),
    refetchOnWindowFocus: false,
  })

  const faqItem = computed(() => data.value?.find((item) => item.id === itemId.value) ?? null)

  watch(
    [isPending, isError, faqItem],
    ([pending, errored, item]) => {
      if (pending) {
        return
      }

      if (errored) {
        errorNotification('base_something_went_wrong')
        router.push('/faq')
        return
      }

      if (!item) {
        errorNotification('faq_item_not_found')
        router.push('/faq')
      }
    },
    { immediate: true },
  )

  useHead({
    title: () => t('faq_edit_page_title'),
  })
</script>

<template>
  <div class="relative flex w-full max-w-250 flex-col">
    <PageDecorationImage class="pointer-events-none absolute top-0 right-0" />
    <EditorPageHeader
      parent-route-name="/faq/"
      parent-label-key="navigation_item_faq"
      :current-label="$t('faq_breadcrumb_edit')"
      data-testid="faq-editor-page-header"
    />
    <div
      v-if="isPending"
      class="flex min-h-40 items-center justify-center py-8"
      data-testid="faq-edit-loading"
      aria-live="polite"
      aria-busy="true"
    >
      <NSpin size="medium" />
    </div>
    <FaqEditor
      v-else-if="faqItem"
      :faq-item="faqItem"
    />
  </div>
</template>
